import { useState, useEffect } from 'react';
import { Box } from 'src/components/UI/Box';
import { Flex } from 'src/components/UI/Flex';
import { StepHeader } from './components/StepHeader';
import { CustomCheckbox } from './components/CustomCheckbox';
import { NavigationButtons } from './components/NavigationButtons';
import { type ImportDataValues } from './types';
import {
  trackOnboardingImportDataBackClick,
  trackOnboardingPortabilityCheckboxClick,
  trackOnboardingImportDataContinueClick,
} from 'src/services/tracking/events/onboarding.events';

interface ImportDataStepProps {
  initialData: ImportDataValues;
  onNext: (data: ImportDataValues) => void;
  onBack: () => void;
}

export function ImportDataStep({ initialData, onNext, onBack }: ImportDataStepProps) {
  const [needsHelp, setNeedsHelp] = useState(initialData.needsHelp);

  const handleContinue = () => {
    trackOnboardingImportDataContinueClick();
    onNext({ needsHelp });
  };

  const handleBack = () => {
    trackOnboardingImportDataBackClick();
    onBack();
  };

  const handleCheckboxChange = (checked: boolean) => {
    trackOnboardingPortabilityCheckboxClick();
    setNeedsHelp(checked);
  };

  return (
    <Box h="100%" display="flex" flexDir="column">
      <StepHeader
        title="Não comece do zero!"
        subtitle="Se você vem de outra plataforma, nós ajudamos a importar seus registros de pacientes, históricos e modelos de receitas para você ganhar tempo."
      />

      <Box
        flex="1"
        minH="0"
        overflowY="auto"
        pr={{ base: '0px', lg: '40px' }}
        css={{
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#FFE4EB',
            borderRadius: '3px',
          },
        }}
      >
        <CustomCheckbox
          isChecked={needsHelp}
          onChange={handleCheckboxChange}
          label="Quero ajuda para importar meus pacientes e históricos"
        />

        {needsHelp && (
          <Flex alignItems="center" gap="12px" mt="16px" p="16px" bg="green.50" borderRadius="8px">
            <Box
              w="24px"
              h="24px"
              borderRadius="full"
              bg="green.500"
              display="flex"
              alignItems="center"
              justifyContent="center"
              flexShrink={0}
            >
              <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                <path
                  d="M13.3333 4L6 11.3333L2.66667 8"
                  stroke="white"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </Box>

            <Box fontFamily={'Ambit'} fontSize="14px" fontWeight="regular" color="gray.800">
              Excelente! Nossa equipe entrará em contato com você em breve.
            </Box>
          </Flex>
        )}
      </Box>

      <NavigationButtons onBack={handleBack} onNext={handleContinue} />
    </Box>
  );
}
